import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash, PencilSimple, MagnifyingGlass, Info, LinkSimple, BookOpen, Archive, ArrowClockwise, Stack, Eye } from '@phosphor-icons/react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useCan } from '@/lib/permissions';
import ServiceLedgerDialog from '@/components/ServiceLedgerDialog';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

export default function ServicesPage() {
  const canEdit = useCan('services', 'edit');
  const canDelete = useCan('services', 'delete');
  const [services, setServices] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editSvc, setEditSvc] = useState(null);
  // Per-service Ledger viewer — opened from the BookOpen icon on each row.
  const [ledgerSvc, setLedgerSvc] = useState(null);
  const [form, setForm] = useState({ name: '', code: '', service_charge: '', govt_fee: '', default_fee_mode: '', is_recurring: false, recurring_interval: '', recurring_interval_months: '', link1: '', link2: '', group_id: '', multi_price: false, tier_mode: 'volume', price_tiers: [] });
  // Service-group master + inline-create state. The "+ New Group" toggle
  // expands a tiny 3-input mini-form (name + link1 + link2) right inside
  // the service dialog so the user never has to leave for /service-groups.
  const [groups, setGroups] = useState([]);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', link1: '', link2: '' });
  const groupName = (gid) => groups.find(g => g.id === gid)?.name || '';
  const [finAccounts, setFinAccounts] = useState([]);
  // Quick tier editor — opened via the "Manage Pricing" action button on a
  // list row. Keeps the operator focused on tiers alone without loading the
  // full service dialog.
  const [pricingSvc, setPricingSvc] = useState(null);
  // Mode for the pricingSvc quick dialog: 'view' (read-only) | 'edit'.
  const [pricingSvcMode, setPricingSvcMode] = useState('edit');
  // Nested popup opened from inside the main Service dialog to author tiers
  // for an unsaved / mid-edit form.
  const [formPricingMode, setFormPricingMode] = useState(null); // null | 'view' | 'edit'

  const fetchServices = useCallback(() => {
    // Load ALL services (including archived ones) so the Services master page
    // can render the archive toggle and clearly show which items are hidden
    // from new bills. The invoice pickers filter separately via
    // `include_archived=false`.
    api.get('/services', { params: { include_archived: true } })
      .then(r => setServices(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);
  useEffect(() => {
    api.get('/service-groups').then(r => setGroups(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    api.get('/finance/accounts').then(r => setFinAccounts(r.data)).catch(console.error);
  }, []);

  const resetForm = () => setForm({ name: '', code: '', service_charge: '', govt_fee: '', default_fee_mode: '', requires_followup: false, is_recurring: false, recurring_interval: '', recurring_interval_months: '', link1: '', link2: '', group_id: '', multi_price: false, tier_mode: 'volume', price_tiers: [] });

  const handleSave = async () => {
    if (!form.name || !form.service_charge) { toast.error('Name and charge required'); return; }
    // Local sanity — backend re-validates + enforces per-tenant uniqueness.
    if (form.code && !/^\d{2,3}$/.test(String(form.code).trim())) {
      toast.error('Code must be 2 or 3 digits (numeric only)');
      return;
    }
    if (form.is_recurring && !form.recurring_interval) { toast.error('Pick a recurring interval'); return; }
    if (form.is_recurring && form.recurring_interval === 'custom' && (!form.recurring_interval_months || parseInt(form.recurring_interval_months) <= 0)) {
      toast.error('Enter custom interval in months');
      return;
    }
    const payload = {
      ...form,
      code: String(form.code || '').trim(),
      service_charge: parseFloat(form.service_charge),
      govt_fee: parseFloat(form.govt_fee) || 0,
      // Send `null` when not recurring or non-custom interval so the backend
      // doesn't store a stray number.
      recurring_interval_months: form.is_recurring && form.recurring_interval === 'custom'
        ? parseInt(form.recurring_interval_months)
        : null,
      // Clear interval when not recurring so historic toggles don't linger.
      recurring_interval: form.is_recurring ? form.recurring_interval : '',
      link1: (form.link1 || '').trim(),
      link2: (form.link2 || '').trim(),
    };
    try {
      if (editSvc) {
        await api.put(`/services/${editSvc.id}`, payload);
        toast.success('Service updated');
      } else {
        await api.post('/services', payload);
        toast.success('Service added');
      }
      setShowAdd(false);
      setEditSvc(null);
      resetForm();
      fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleEdit = (svc) => {
    setEditSvc(svc);
    setForm({ name: svc.name, code: svc.code || '', service_charge: String(svc.service_charge), govt_fee: String(svc.govt_fee), default_fee_mode: svc.default_fee_mode || '', requires_followup: svc.requires_followup || false, is_recurring: svc.is_recurring || false, recurring_interval: svc.recurring_interval || '', recurring_interval_months: svc.recurring_interval_months ? String(svc.recurring_interval_months) : '', link1: svc.link1 || '', link2: svc.link2 || '', group_id: svc.group_id || '', multi_price: !!svc.multi_price, tier_mode: svc.tier_mode || 'volume', price_tiers: Array.isArray(svc.price_tiers) ? svc.price_tiers : [] });
    setShowAdd(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    await api.delete(`/services/${id}`);
    toast.success('Service deleted');
    fetchServices();
  };

  const handleToggleArchive = async (svc) => {
    const nextState = !svc.archived;
    const verb = nextState ? 'Archive' : 'Unarchive';
    const msg = nextState
      ? `Archive "${svc.name}"? It will be hidden from the item picker on New / Edit Invoice. Existing invoices / ledgers stay intact.`
      : `Unarchive "${svc.name}"? It will become selectable on new bills again.`;
    if (!window.confirm(msg)) return;
    try {
      await api.put(`/services/${svc.id}/archive`, { archived: nextState });
      toast.success(`${svc.name} ${verb.toLowerCase()}d`);
      fetchServices();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${verb.toLowerCase()}`);
    }
  };

  return (
    <TooltipProvider delayDuration={150}>
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="services-page">
      <div className="flex items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search services..."
              className="rounded-xl h-9 text-sm pl-8 w-64 border-[#E4E4E7]"
              data-testid="service-search-input"
            />
          </div>
          {canEdit && (
            <Button onClick={() => { resetForm(); setEditSvc(null); setShowAdd(true); }}
              className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="add-service-btn">
              <Plus size={16} className="mr-2" /> Add Service
            </Button>
          )}
        </div>
      </div>

      {/* Category filter removed */}

      <Card className="rounded-xl border-[#E4E4E7] shadow-sm">
        <CardContent className="p-0">
          {(() => {
            const q = search.trim().toLowerCase();
            const filtered = q
              ? services.filter(s =>
                  s.name.toLowerCase().includes(q) ||
                  (s.code || '').toLowerCase().includes(q) ||
                  (groupName(s.group_id) || '').toLowerCase().includes(q)
                )
              : services;
            if (loading) return <p className="p-4 text-sm text-[#52525B]">Loading...</p>;
            if (filtered.length === 0) {
              return (
                <p className="p-8 text-center text-sm text-[#52525B]">
                  {q
                    ? `No services match "${search}".`
                    : `No services found. Add your first service.`}
                </p>
              );
            }
            return (
            <Table>
              <TableHeader>
                <TableRow className="border-[#E4E4E7]">
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Name</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Group</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Service Charge</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Govt Fee</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Total</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-center">Default Fee Mode</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-center">Follow-up</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-center">Links</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(s => (
                  <TableRow key={s.id} className={`border-[#E4E4E7] ${s.archived ? 'bg-[#FAFAFA] opacity-60' : ''}`} data-testid={`service-row-${s.id}`}>
                    <TableCell className="text-sm font-medium">
                      <div className="flex items-center gap-1.5">
                        {/* Override dot — flagged when this service holds a
                            custom link that diverges from its group. Tooltip
                            reveals the situation; the row otherwise behaves
                            normally. */}
                        {s.link_overridden && (
                          <span
                            className="inline-block w-2 h-2 rounded-full bg-[#F59E0B]"
                            title="Custom link — differs from group default"
                            data-testid={`svc-override-dot-${s.id}`}
                          />
                        )}
                        {s.code && (
                          <span
                            className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-md text-[10px] font-bold font-mono bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFDBFE]"
                            data-testid={`service-code-${s.id}`}
                          >{s.code}</span>
                        )}
                        <span className={s.archived ? 'line-through' : ''}>{s.name}</span>
                        {s.multi_price && (
                          <Badge
                            className={`ml-1 rounded-md text-[9px] uppercase tracking-wide border ${s.tier_mode === 'price' ? 'bg-[#F5F3FF] text-[#6D28D9] border-[#DDD6FE]' : 'bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]'}`}
                            variant="outline"
                            data-testid={`service-mp-tag-${s.id}`}
                          >
                            {(s.price_tiers || []).length} · {s.tier_mode === 'price' ? 'Variants' : 'Volume'}
                          </Badge>
                        )}
                        {s.archived && (
                          <Badge className="ml-1 rounded-md text-[9px] uppercase tracking-wide bg-[#F4F4F5] text-[#71717A] border-0" variant="outline" data-testid={`service-archived-tag-${s.id}`}>
                            Archived
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">
                      {s.group_id ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-[#EFF6FF] text-[#1D4ED8] border border-[#BFDBFE]">{groupName(s.group_id) || '—'}</span>
                      ) : <span className="text-[#A1A1AA]">—</span>}
                    </TableCell>
                    <TableCell className="text-sm text-right font-mono">{fmt(s.service_charge)}</TableCell>
                    <TableCell className="text-sm text-right font-mono">{fmt(s.govt_fee)}</TableCell>
                    <TableCell className="text-sm text-right font-mono font-semibold">{fmt(s.service_charge + s.govt_fee)}</TableCell>
                    <TableCell className="text-center">
                      {s.default_fee_mode ? (() => {
                        const acc = finAccounts.find(a => a.id === s.default_fee_mode);
                        return (
                          <Badge className="font-medium border uppercase tracking-wide text-[10px] bg-[#F0F9FF] text-[#0369A1] border-[#BAE6FD]" variant="outline">
                            {acc ? acc.name : s.default_fee_mode.slice(0, 6)}
                          </Badge>
                        );
                      })() : <span className="text-[11px] text-[#A1A1AA]">—</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      {s.requires_followup ? (
                        <Badge className="font-medium border uppercase tracking-wide text-[10px] bg-[#FEF3C7] text-[#D97706] border-[#FDE68A]" variant="outline">Required</Badge>
                      ) : <span className="text-[11px] text-[#A1A1AA]">No</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        {s.link1 ? (
                          <a href={s.link1} target="_blank" rel="noopener noreferrer"
                            title={`Link 1 (Invoice page): ${s.link1}`}
                            data-testid={`service-link1-badge-${s.id}`}
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#E0F2FE] text-[#0369A1] hover:bg-[#BAE6FD] border border-[#BAE6FD]">
                            <LinkSimple size={10} weight="bold" /> L1
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#F4F4F5] text-[#A1A1AA] border border-[#E4E4E7]" title="Link 1 not set">
                            L1
                          </span>
                        )}
                        {s.link2 ? (
                          <a href={s.link2} target="_blank" rel="noopener noreferrer"
                            title={`Link 2 (Follow-up page): ${s.link2}`}
                            data-testid={`service-link2-badge-${s.id}`}
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#DCFCE7] text-[#15803D] hover:bg-[#BBF7D0] border border-[#BBF7D0]">
                            <LinkSimple size={10} weight="bold" /> L2
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-[#F4F4F5] text-[#A1A1AA] border border-[#E4E4E7]" title="Link 2 not set">
                            L2
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {canEdit && s.multi_price && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-[#0EA5E9] hover:text-[#0369A1] hover:bg-[#F0F9FF]"
                            onClick={() => { setPricingSvcMode('view'); setPricingSvc(s); }}
                            title={`View ${(s.price_tiers || []).length} tier${(s.price_tiers || []).length === 1 ? '' : 's'}`}
                            data-testid={`view-tiers-${s.id}`}
                          >
                            <Eye size={14} />
                          </Button>
                        )}
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-7 w-7 p-0 ${s.multi_price ? 'text-[#F97316] hover:text-[#EA580C] hover:bg-[#FFF7ED]' : 'text-[#71717A] hover:text-[#0A0A0A] hover:bg-[#F4F4F5]'}`}
                            onClick={() => { setPricingSvcMode('edit'); setPricingSvc(s); }}
                            title={s.multi_price ? `Edit pricing tiers (${(s.price_tiers || []).length})` : 'Set volume/price-based tiers'}
                            data-testid={`pricing-service-${s.id}`}
                          >
                            <Stack size={14} weight={s.multi_price ? 'fill' : 'regular'} />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-[#7C3AED] hover:text-[#6D28D9] hover:bg-[#F5F3FF]"
                          onClick={() => setLedgerSvc(s)}
                          title={`View ledger (${(s.invoice_count || 0) + (s.draft_count || 0) + (s.token_count || 0)} txn)`}
                          data-testid={`ledger-service-${s.id}`}
                        >
                          <BookOpen size={14} />
                        </Button>
                        {canEdit && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEdit(s)} data-testid={`edit-service-${s.id}`}>
                            <PencilSimple size={14} />
                          </Button>
                        )}
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-7 w-7 p-0 ${s.archived ? 'text-[#10B981] hover:text-[#059669] hover:bg-[#ECFDF5]' : 'text-[#F59E0B] hover:text-[#D97706] hover:bg-[#FFFBEB]'}`}
                            onClick={() => handleToggleArchive(s)}
                            title={s.archived ? 'Unarchive — make selectable on new bills again' : 'Archive — hide from New / Edit Invoice item picker'}
                            data-testid={s.archived ? `unarchive-service-${s.id}` : `archive-service-${s.id}`}
                          >
                            {s.archived ? <ArrowClockwise size={14} /> : <Archive size={14} />}
                          </Button>
                        )}
                        {canDelete && !s.in_use && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#EF4444]" onClick={() => handleDelete(s.id)} data-testid={`delete-service-${s.id}`}>
                            <Trash size={14} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            );
          })()}
        </CardContent>
      </Card>

      <Dialog open={showAdd} onOpenChange={(v) => { setShowAdd(v); if (!v) setEditSvc(null); }}>
        <DialogContent className="rounded-xl border-[#E4E4E7] max-w-2xl h-[95vh] flex flex-col" data-testid="service-add-dialog">
          <DialogHeader>
            <DialogTitle>{editSvc ? 'Edit Service' : 'Add Service'}</DialogTitle>
            <DialogDescription>Enter service details with pricing</DialogDescription>
          </DialogHeader>
          {/* Scrollable body — keeps the DialogFooter's Save button pinned at
              the bottom on tall forms (multi-price, recurring, links…). */}
          <div className="space-y-3 flex-1 overflow-y-auto pr-2 -mr-2">
            <div className="grid grid-cols-[110px_1fr] gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Code</Label>
                <Input
                  value={form.code}
                  onChange={e => setForm({ ...form, code: e.target.value.replace(/[^0-9]/g, '').slice(0, 3) })}
                  placeholder="e.g. 42"
                  inputMode="numeric"
                  maxLength={3}
                  className="rounded-lg mt-1 font-mono"
                  data-testid="service-code-input"
                />
                <p className="text-[10px] text-[#A1A1AA] mt-1">2-3 digits · unique · optional</p>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-lg mt-1" data-testid="service-name-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Service Charge *</Label>
                <Input type="number" value={form.service_charge} onChange={e => setForm({ ...form, service_charge: e.target.value })} className="rounded-lg mt-1" data-testid="service-charge-input" />
                <p className="text-[10px] text-[#A1A1AA] mt-1">
                  {form.multi_price ? 'Fallback price when qty is outside every tier.' : 'Per-unit price.'}
                </p>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Govt Fee</Label>
                <Input type="number" value={form.govt_fee} onChange={e => setForm({ ...form, govt_fee: e.target.value })} className="rounded-lg mt-1" data-testid="service-govtfee-input" />
              </div>
            </div>

            {/* Multi-price toggle — the actual tier editor lives in a nested
                popup opened via the View/Edit buttons below. */}
            <div className="rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] p-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.multi_price}
                  onChange={e => setForm({
                    ...form,
                    multi_price: e.target.checked,
                    // Seed a starter tier so the popup has something to fill.
                    price_tiers: e.target.checked && (!form.price_tiers || form.price_tiers.length === 0)
                      ? (form.tier_mode === 'price'
                          ? [{ label: '', price: '' }]
                          : [{ min_qty: 1, max_qty: '', price: '' }])
                      : form.price_tiers,
                  })}
                  className="h-4 w-4 accent-[#F97316]"
                  data-testid="service-multi-price-toggle"
                />
                <span className="text-sm font-semibold text-[#0A0A0A]">Multi-price</span>
              </label>
              <p className="text-[10px] text-[#A1A1AA] mt-1 ml-6">
                Enable volume-based (qty range) or price-based (named variants) tier pricing.
              </p>
              {form.multi_price && (
                <div className="mt-3 flex items-center flex-wrap gap-2 ml-6">
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-[#E4E4E7] text-xs">
                    <span className="text-[#71717A]">Mode:</span>
                    <b className="text-[#0A0A0A] capitalize">{form.tier_mode === 'price' ? 'Price-based' : 'Volume-based'}</b>
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-[#E4E4E7] text-xs">
                    <b className="text-[#0A0A0A]">{(form.price_tiers || []).length}</b>
                    <span className="text-[#71717A]">tiers configured</span>
                  </span>
                  <div className="flex-1" />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setFormPricingMode('view')}
                    disabled={!(form.price_tiers || []).length}
                    className="h-8 text-xs"
                    data-testid="form-tiers-view"
                  >
                    <Eye size={12} className="mr-1" /> View
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => setFormPricingMode('edit')}
                    className="h-8 text-xs bg-[#F97316] text-white hover:bg-[#EA580C]"
                    data-testid="form-tiers-edit"
                  >
                    <PencilSimple size={12} className="mr-1" /> Edit tiers
                  </Button>
                </div>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Group (optional)</Label>
                {!showNewGroup && (
                  <button type="button" onClick={() => setShowNewGroup(true)} className="text-[10px] text-[#3B82F6] hover:text-[#2563EB] font-semibold" data-testid="service-add-group-btn">+ New Group</button>
                )}
              </div>
              {showNewGroup ? (
                <div className="space-y-2 mt-1 p-2 rounded-lg bg-[#EFF6FF] border border-[#DBEAFE]">
                  <Input
                    value={newGroup.name}
                    onChange={e => setNewGroup(g => ({ ...g, name: e.target.value }))}
                    placeholder="Group name (e.g. eDistrict)"
                    className="rounded-lg h-9 text-sm"
                    autoFocus
                    data-testid="service-new-group-name"
                  />
                  <Input
                    value={newGroup.link1}
                    onChange={e => setNewGroup(g => ({ ...g, link1: e.target.value }))}
                    placeholder="Link 1 (Invoice shortcut URL)"
                    className="rounded-lg h-9 text-sm"
                    data-testid="service-new-group-link1"
                  />
                  <Input
                    value={newGroup.link2}
                    onChange={e => setNewGroup(g => ({ ...g, link2: e.target.value }))}
                    placeholder="Link 2 (Follow-up shortcut URL)"
                    className="rounded-lg h-9 text-sm"
                    data-testid="service-new-group-link2"
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      className="h-8 px-3 text-xs bg-[#3B82F6] text-white hover:bg-[#2563EB]"
                      onClick={async () => {
                        const n = newGroup.name.trim();
                        if (!n) { toast.error('Group name required'); return; }
                        try {
                          const { data } = await api.post('/service-groups', { name: n, link1: newGroup.link1.trim(), link2: newGroup.link2.trim() });
                          setGroups(gs => [...gs, data]);
                          // Auto-copy the group's links onto the current service form.
                          setForm(f => ({ ...f, group_id: data.id, link1: data.link1, link2: data.link2 }));
                          setShowNewGroup(false);
                          setNewGroup({ name: '', link1: '', link2: '' });
                          toast.success(`Group "${data.name}" created`);
                        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add group'); }
                      }}
                      data-testid="service-save-group-btn"
                    >Save Group</Button>
                    <Button type="button" size="sm" variant="outline" onClick={() => { setShowNewGroup(false); setNewGroup({ name: '', link1: '', link2: '' }); }} className="h-8 px-3 text-xs">Cancel</Button>
                  </div>
                </div>
              ) : (
                <Select value={form.group_id || '__none__'} onValueChange={v => {
                  if (v === '__none__') { setForm(f => ({ ...f, group_id: '' })); return; }
                  const grp = groups.find(g => g.id === v);
                  // On group pick, auto-fill the link inputs (user can still tweak before saving).
                  setForm(f => ({ ...f, group_id: v, link1: grp?.link1 || f.link1, link2: grp?.link2 || f.link2 }));
                }}>
                  <SelectTrigger className="rounded-lg mt-1" data-testid="service-group-select"><SelectValue placeholder="Ungrouped" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Ungrouped</SelectItem>
                    {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">Default Gov Fee Mode</Label>
              <Select value={form.default_fee_mode} onValueChange={v => setForm({ ...form, default_fee_mode: v })}>
                <SelectTrigger className="rounded-lg mt-1" data-testid="service-fee-mode-select"><SelectValue placeholder="Select account..." /></SelectTrigger>
                <SelectContent>{finAccounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.type})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="requires-followup" checked={form.requires_followup} onChange={e => setForm({ ...form, requires_followup: e.target.checked })} className="rounded" data-testid="service-followup-checkbox" />
              <Label htmlFor="requires-followup" className="text-sm text-[#52525B] cursor-pointer">Requires Follow-up / Processing</Label>
            </div>

            {/* Phase D — Optional URL links surfaced on Invoice / Follow-Up. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B] inline-flex items-center gap-1 cursor-help">
                      Link 1 <Info size={11} className="text-[#A1A1AA]" />
                    </Label>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-[#0A0A0A] text-white border-0 text-xs">For Invoice Page link</TooltipContent>
                </Tooltip>
                <Input value={form.link1} onChange={e => setForm({ ...form, link1: e.target.value })}
                  placeholder="https://…" className="rounded-lg mt-1" data-testid="service-link1-input" />
              </div>
              <div>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B] inline-flex items-center gap-1 cursor-help">
                      Link 2 <Info size={11} className="text-[#A1A1AA]" />
                    </Label>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-[#0A0A0A] text-white border-0 text-xs">For Follow-up Page link</TooltipContent>
                </Tooltip>
                <Input value={form.link2} onChange={e => setForm({ ...form, link2: e.target.value })}
                  placeholder="https://…" className="rounded-lg mt-1" data-testid="service-link2-input" />
              </div>
            </div>

            {/* Recurring subscription — when enabled, every invoice that
                contains this service shows up on the Recurring page once
                the renewal date approaches. */}
            <div className="rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] p-3 space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is-recurring"
                  checked={!!form.is_recurring}
                  onChange={e => setForm({ ...form, is_recurring: e.target.checked })}
                  className="rounded h-4 w-4 accent-[#3B82F6]"
                  data-testid="service-recurring-checkbox"
                />
                <Label htmlFor="is-recurring" className="text-sm font-semibold text-[#0A0A0A] cursor-pointer">
                  Recurring (subscription-style)
                </Label>
              </div>
              {form.is_recurring && (
                <div className="grid grid-cols-2 gap-3 pl-6">
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#52525B]">Renewal Interval</Label>
                    <Select value={form.recurring_interval} onValueChange={v => setForm({ ...form, recurring_interval: v })}>
                      <SelectTrigger className="rounded-lg mt-1 h-9" data-testid="service-recurring-interval">
                        <SelectValue placeholder="Choose interval..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly (1 month)</SelectItem>
                        <SelectItem value="quarterly">Quarterly (3 months)</SelectItem>
                        <SelectItem value="half_yearly">Half-Yearly (6 months)</SelectItem>
                        <SelectItem value="yearly">Yearly (12 months)</SelectItem>
                        <SelectItem value="custom">Custom (months)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.recurring_interval === 'custom' && (
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#52525B]">Custom Months</Label>
                      <Input
                        type="number"
                        min="1"
                        value={form.recurring_interval_months}
                        onChange={e => setForm({ ...form, recurring_interval_months: e.target.value })}
                        placeholder="e.g. 18"
                        className="rounded-lg mt-1 h-9 font-mono"
                        data-testid="service-recurring-months"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handleSave} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="save-service-btn">
              {editSvc ? 'Update' : 'Save'} Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ServiceLedgerDialog
        service={ledgerSvc}
        open={!!ledgerSvc}
        onOpenChange={(v) => { if (!v) setLedgerSvc(null); }}
      />
      <PricingDialog
        service={pricingSvc}
        mode={pricingSvcMode}
        onClose={() => setPricingSvc(null)}
        onSaved={(updated) => {
          setServices(list => list.map(x => x.id === updated.id ? { ...x, multi_price: updated.multi_price, tier_mode: updated.tier_mode, price_tiers: updated.price_tiers } : x));
          setPricingSvc(null);
          toast.success('Pricing tiers saved');
        }}
      />
      {/* Nested tier popup opened from inside the main Service dialog. */}
      <FormTierPopup
        open={!!formPricingMode}
        mode={formPricingMode || 'edit'}
        multiPrice={form.multi_price}
        tierMode={form.tier_mode}
        tiers={form.price_tiers || []}
        onClose={() => setFormPricingMode(null)}
        onSave={({ tier_mode, price_tiers }) => {
          setForm({ ...form, tier_mode, price_tiers });
          setFormPricingMode(null);
        }}
      />
    </div>
    </TooltipProvider>
  );
}


/**
 * Mode-aware tier editor. Supports two shapes:
 *   * `volume` — [{min_qty, max_qty, price}]  qty-based ranges.
 *   * `price`  — [{label, price}]             named variants (e.g. "4x6").
 * Set `readOnly` to render a compact non-editable listing (used from the
 * "View" popup).
 */
function TierEditor({ mode, tiers, onChange, readOnly = false }) {
  const isPrice = mode === 'price';

  const update = (idx, patch) => {
    if (readOnly) return;
    onChange(tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };
  const remove = (idx) => onChange(tiers.filter((_, i) => i !== idx));
  const add = () => {
    if (isPrice) {
      onChange([...(tiers || []), { label: '', price: '' }]);
    } else {
      const last = tiers[tiers.length - 1];
      const seedMin = last && last.max_qty ? Number(last.max_qty) + 1 : (tiers.length ? '' : 1);
      onChange([...(tiers || []), { min_qty: seedMin, max_qty: '', price: '' }]);
    }
  };

  // ---------- View-only rendering ----------------------------------------
  if (readOnly) {
    return (
      <div className="mt-2 rounded-lg border border-[#E4E4E7] bg-white overflow-hidden" data-testid="tier-editor-view">
        <div className={`grid ${isPrice ? 'grid-cols-2' : 'grid-cols-3'} gap-0 text-[10px] uppercase tracking-[0.12em] font-semibold text-[#71717A] bg-[#FAFAFA] border-b border-[#E4E4E7] px-3 py-2`}>
          {isPrice ? (<><span>Variant</span><span className="text-right">Price</span></>) : (<><span>Min Qty</span><span>Max Qty</span><span className="text-right">Price / unit</span></>)}
        </div>
        {(tiers || []).length === 0 && (
          <div className="px-3 py-4 text-xs text-[#A1A1AA] text-center">No tiers configured yet.</div>
        )}
        {(tiers || []).map((t, i) => (
          <div key={i} className={`grid ${isPrice ? 'grid-cols-2' : 'grid-cols-3'} gap-0 text-sm px-3 py-2 border-t border-[#F4F4F5] first:border-t-0`}>
            {isPrice ? (
              <>
                <span className="font-medium text-[#0A0A0A]">{t.label}</span>
                <span className="text-right font-mono text-[#10B981]">₹{Number(t.price || 0).toLocaleString('en-IN')}</span>
              </>
            ) : (
              <>
                <span className="font-mono text-[#0A0A0A]">{t.min_qty ?? '-'}</span>
                <span className="font-mono text-[#0A0A0A]">{t.max_qty == null || t.max_qty === '' ? '∞' : t.max_qty}</span>
                <span className="text-right font-mono text-[#10B981]">₹{Number(t.price || 0).toLocaleString('en-IN')}</span>
              </>
            )}
          </div>
        ))}
      </div>
    );
  }

  // ---------- Editable rendering -----------------------------------------
  const grid = isPrice ? 'grid-cols-[1fr_100px_28px]' : 'grid-cols-[80px_80px_100px_28px]';
  return (
    <div className="mt-3 space-y-2" data-testid="tier-editor">
      <div className={`grid ${grid} gap-2 items-center px-1`}>
        {isPrice ? (
          <>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#71717A]">Variant Label</Label>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#71717A]">Price</Label>
            <span />
          </>
        ) : (
          <>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#71717A]">Min Qty</Label>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#71717A]">Max Qty</Label>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#71717A]">Price / unit</Label>
            <span />
          </>
        )}
      </div>
      {(tiers || []).map((t, idx) => (
        <div key={idx} className={`grid ${grid} gap-2 items-center`} data-testid={`tier-row-${idx}`}>
          {isPrice ? (
            <Input
              value={t.label ?? ''}
              placeholder="e.g. 4x6, Basic, Adult"
              onChange={e => update(idx, { label: e.target.value })}
              className="h-9 rounded-md text-sm"
              data-testid={`tier-label-${idx}`}
            />
          ) : (
            <>
              <Input
                type="number" min="1"
                value={t.min_qty ?? ''}
                onChange={e => update(idx, { min_qty: e.target.value.replace(/[^0-9]/g, '') })}
                className="h-9 rounded-md font-mono text-sm"
                data-testid={`tier-min-${idx}`}
              />
              <Input
                type="number" min="1"
                value={t.max_qty ?? ''}
                placeholder="∞"
                onChange={e => update(idx, { max_qty: e.target.value.replace(/[^0-9]/g, '') })}
                className="h-9 rounded-md font-mono text-sm"
                data-testid={`tier-max-${idx}`}
              />
            </>
          )}
          <Input
            type="number" min="0" step="0.01"
            value={t.price ?? ''}
            onChange={e => update(idx, { price: e.target.value })}
            className="h-9 rounded-md font-mono text-sm"
            data-testid={`tier-price-${idx}`}
          />
          <Button
            type="button" variant="ghost" size="sm"
            className="h-8 w-8 p-0 text-[#EF4444] hover:text-[#DC2626] hover:bg-[#FEF2F2]"
            onClick={() => remove(idx)}
            disabled={tiers.length <= 1}
            data-testid={`tier-remove-${idx}`}
            title="Remove tier"
          >
            <Trash size={12} />
          </Button>
        </div>
      ))}
      <Button
        type="button" size="sm" variant="outline"
        className="w-full h-8 text-xs mt-1"
        onClick={add}
        data-testid="tier-add"
      >
        <Plus size={12} className="mr-1" /> Add tier
      </Button>
      <p className="text-[10px] text-[#A1A1AA]">
        {isPrice
          ? 'Each variant becomes its own row in the invoice item picker.'
          : 'Leave Max blank on the LAST tier to mean "unlimited". Tiers must not overlap.'}
      </p>
    </div>
  );
}

/**
 * Mode + tier-mode picker header, shared between the FormTierPopup and the
 * saved-service PricingDialog. Split out so the visual language stays
 * identical in both places.
 */
function TierModePicker({ mode, onChange, disabled }) {
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="tier-mode-picker">
      <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#71717A]">Tier Style</span>
      {['volume', 'price'].map((m) => (
        <button
          key={m}
          type="button"
          disabled={disabled}
          onClick={() => onChange(m)}
          className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${mode === m ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#52525B] border-[#E4E4E7] hover:border-[#A1A1AA]'} disabled:opacity-50 disabled:cursor-not-allowed`}
          data-testid={`tier-mode-${m}`}
        >
          {m === 'price' ? 'Price-based' : 'Volume-based'}
        </button>
      ))}
    </div>
  );
}

/**
 * Nested popup opened FROM WITHIN the main Service Add/Edit dialog. It lets
 * the operator author tiers for the service being edited without hitting
 * the backend directly — final commit happens when the outer dialog saves.
 */
function FormTierPopup({ open, mode, multiPrice, tierMode, tiers, onSave, onClose }) {
  const [localMode, setLocalMode] = useState(tierMode || 'volume');
  const [localTiers, setLocalTiers] = useState(tiers || []);

  useEffect(() => {
    if (!open) return;
    setLocalMode(tierMode || 'volume');
    setLocalTiers((tiers || []).map(t => (
      tierMode === 'price'
        ? { label: t.label ?? '', price: t.price ?? '' }
        : { min_qty: t.min_qty ?? 1, max_qty: t.max_qty == null ? '' : t.max_qty, price: t.price ?? '' }
    )));
  }, [open, tierMode, tiers]);

  const switchMode = (m) => {
    setLocalMode(m);
    setLocalTiers(m === 'price'
      ? [{ label: '', price: '' }]
      : [{ min_qty: 1, max_qty: '', price: '' }]);
  };

  const commit = () => onSave({ tier_mode: localMode, price_tiers: localTiers });

  if (!multiPrice) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-lg" data-testid="form-tier-popup">
        <DialogHeader>
          <DialogTitle>{mode === 'view' ? 'View Pricing Tiers' : 'Edit Pricing Tiers'}</DialogTitle>
          <DialogDescription>
            {mode === 'view'
              ? 'Read-only preview of the current pricing tiers.'
              : 'Choose a tier style and configure how this service is priced.'}
          </DialogDescription>
        </DialogHeader>

        <TierModePicker mode={localMode} onChange={switchMode} disabled={mode === 'view'} />

        <TierEditor mode={localMode} tiers={localTiers} onChange={setLocalTiers} readOnly={mode === 'view'} />

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="form-tier-popup-close">
            {mode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {mode === 'edit' && (
            <Button onClick={commit} className="bg-[#F97316] text-white hover:bg-[#EA580C]" data-testid="form-tier-popup-save">
              Apply
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Quick-edit / view dialog for a SAVED service — opened from the list row
 * actions. `mode='view'` renders read-only; `mode='edit'` (default) lets the
 * operator change the mode, tiers, or turn multi-price off. Persists via the
 * dedicated `/services/{id}/tiers` endpoint.
 */
function PricingDialog({ service, mode = 'edit', onClose, onSaved }) {
  const [multi, setMulti] = useState(false);
  const [localMode, setLocalMode] = useState('volume');
  const [tiers, setTiers] = useState([]);
  const [saving, setSaving] = useState(false);
  const readOnly = mode === 'view';

  useEffect(() => {
    if (!service) return;
    setMulti(!!service.multi_price);
    setLocalMode(service.tier_mode || 'volume');
    const src = Array.isArray(service.price_tiers) && service.price_tiers.length
      ? service.price_tiers
      : (service.tier_mode === 'price' ? [{ label: '', price: '' }] : [{ min_qty: 1, max_qty: '', price: '' }]);
    setTiers(src.map(t => (
      (service.tier_mode || 'volume') === 'price'
        ? { label: t.label ?? '', price: t.price ?? '' }
        : { min_qty: t.min_qty ?? 1, max_qty: t.max_qty == null ? '' : t.max_qty, price: t.price ?? '' }
    )));
  }, [service]);

  const switchMode = (m) => {
    setLocalMode(m);
    setTiers(m === 'price' ? [{ label: '', price: '' }] : [{ min_qty: 1, max_qty: '', price: '' }]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = multi
        ? tiers.map(t => (localMode === 'price'
            ? { label: String(t.label || '').trim(), price: parseFloat(t.price) || 0 }
            : {
                min_qty: parseInt(t.min_qty) || 0,
                max_qty: t.max_qty === '' || t.max_qty == null ? null : parseInt(t.max_qty),
                price: parseFloat(t.price) || 0,
              }))
        : [];
      const { data } = await api.put(`/services/${service.id}/tiers`, {
        multi_price: multi,
        tier_mode: multi ? localMode : 'volume',
        price_tiers: clean,
      });
      onSaved({ id: service.id, ...data });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save tiers');
    } finally {
      setSaving(false);
    }
  };

  const open = !!service;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-lg" data-testid="pricing-dialog">
        <DialogHeader>
          <DialogTitle>{readOnly ? 'View Pricing Tiers' : 'Pricing Tiers'}</DialogTitle>
          <DialogDescription>
            {service?.name}
            {service?.code && <span className="ml-2 font-mono text-[#1D4ED8]">[{service.code}]</span>}
          </DialogDescription>
        </DialogHeader>

        <label className={`flex items-center gap-2 ${readOnly ? '' : 'cursor-pointer'}`}>
          <input
            type="checkbox"
            checked={multi}
            disabled={readOnly}
            onChange={e => setMulti(e.target.checked)}
            className="h-4 w-4 accent-[#F97316]"
            data-testid="pricing-dialog-toggle"
          />
          <span className="text-sm font-semibold text-[#0A0A0A]">Multi-price</span>
        </label>
        <p className="text-[11px] text-[#71717A]">
          Volume-based snaps the per-unit price by qty. Price-based lets the operator pick a named variant at bill time.
        </p>

        {multi && (
          <>
            <TierModePicker mode={localMode} onChange={switchMode} disabled={readOnly} />
            <TierEditor mode={localMode} tiers={tiers} onChange={setTiers} readOnly={readOnly} />
          </>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="pricing-dialog-cancel">
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {!readOnly && (
            <Button
              onClick={save}
              disabled={saving}
              className="bg-[#F97316] text-white hover:bg-[#EA580C]"
              data-testid="pricing-dialog-save"
            >
              {saving ? 'Saving…' : 'Save tiers'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

